<template>
  <LoadingSpinner v-if="isLoading" />

  <div v-else-if="isError" class="no-print">
    <div class="text-center mt-5">
      <h1 class="text-2xl">Error loading change order</h1>
      <p class="text-lg">Please try again later</p>
    </div>
  </div>

  <div v-else class="no-print">
    <!-- header -->
    <div
      class="justify-between items-center bg-white p-4 shadow-md fixed top-0 left-0 right-0 z-[10000]"
      :class="isAuthenticated ? 'hidden lg:flex' : 'flex'"
    >
      <!-- logo -->
      <div>
        <img :src="logo" alt="uttrly logo" class="h-10" />
      </div>

      <!-- menu items -->
      <!-- mobile -->
      <div class="md:hidden flex items-end gap-4">
        <BaseDropdown
          :items="[
            { title: 'Download', action: () => download() },
            { title: 'Print', action: () => printChangeOrder() }
          ]"
        />

        <!-- accept, decline, request question -->
        <BaseDropdown
          v-if="changeOrder.status === PROPOSAL_STATUS.AWAITING_ACCEPTANCE && !isPending"
          title="Respond"
          right
          :items="[
            { title: 'Accept', action: () => openSignatureModal = true, disabled: preview },
            { title: 'Decline', action: () => declineChangeOrder(), disabled: preview },
            { title: 'Request Question', action: () => gotoComment() }
          ]"
        />

        <div
          v-if="isPending"
          class="flex items-center mx-4"
        >
          <!-- spinner -->
           <span class="i-heroicons-arrow-path animate-spin" />
        </div>

        <div
          v-if="!isPending && changeOrder.status !== PROPOSAL_STATUS.AWAITING_ACCEPTANCE"
          class="self-center"
        >
          <ProposalStatusBadge :status="changeOrder.status" class="mx-2" />
        </div>
      </div>

      <!-- desktop -->
      <div class="hidden md:flex items-center gap-4">
        <div class="flex items-center gap-4">
          <BaseButton
            variant="base"
            @click="download()"
          >
            <span class="i-heroicons-arrow-down-tray" />
          </BaseButton>

          <BaseButton
            variant="base"
            @click="printChangeOrder()"
          >
            <span class="i-heroicons-printer" />
          </BaseButton>
        </div>

        <!-- accept, decline, request question -->
        <BaseButton
          v-if="changeOrder.status === PROPOSAL_STATUS.AWAITING_ACCEPTANCE && !isPending"
          variant="primary"
          text="Accept"
          :disabled="preview"
          @click="openSignatureModal = true"
        />

        <BaseButton
          v-if="changeOrder.status === PROPOSAL_STATUS.AWAITING_ACCEPTANCE && !isPending"
          variant="danger"
          text="Decline"
          :disabled="preview"
          @click="declineChangeOrder()"
        />

        <div v-if="isPending" class="flex items-center mx-4">
          <!-- spinner -->
          <span class="i-heroicons-arrow-path animate-spin" />
        </div>

        <BaseButton
          variant="base"
          text="Request Question"
          @click="gotoComment()"
        />

        <div
          v-if="!isPending && changeOrder.status !== PROPOSAL_STATUS.AWAITING_ACCEPTANCE"
          class="self-center"
        >
          <ProposalStatusBadge :status="changeOrder.status" class="mx-2" />
        </div>
      </div>
    </div>

    <!-- change order -->
    <div class="w-full">
      <div
        class="max-w-screen-lg mx-4 md:mx-8 lg:mx-auto"
        :class="isAuthenticated ? '' : 'mt-20'"
      >
        <div
          class="mb-4 md:mb-12"
          :class="isAuthenticated ? '' : 'text-white pt-10'"
        >
          <h2 class="">Change Order for {{ project.name }}</h2>
          <span class="mt-2 text-base inline-block">{{ project.address }}</span>
        </div>

        <!-- change order details -->
        <div class="paper md:mt-4 px-6 py-8 rounded-lg text-sm">
          <!-- header informations -->
          <div class="grid grid-cols-2">
            <!-- customer informations -->
            <div>
              <!-- change order ID -->
              <div class="mb-7">
                <h2 class="text-slate-500 inline-block">Change Order #</h2>
                <h2 class="font-bold inline-block">{{ changeOrder.contractId }}</h2>
              </div>

              <!-- customer informations -->
              <div class="mb-5">
                <span class="block mb-1 font-bold">{{ customer.name }}</span>
                <span class="block mb-1">{{ customer.email }}</span>
                <span
                  v-if="customer.phone"
                  class="block mb-1"
                >{{ customer.phone }}</span>
              </div>
            </div>

            <!-- log and company informations -->
            <div>
              <!-- logo, address -->
              <div class="flex justify-end mb-7">
                <img
                  :src="logo"
                  alt="Uttrly"
                />
              </div>
              <div class="text-right">
                <span class="block mb-2">11810 Grand Park Ave, Suite 500</span>
                <span class="block mb-2">North Bethesda, MD 20852</span>
                <span class="block">202.681.0825</span>
              </div>
            </div>
          </div>

          <!-- change order date and valid until -->
          <div class="flex gap-4">
            <span>
              <b>Change Order date:</b> {{ getLocalDate(changeOrder.contractDate) }}
            </span>
          </div>

          <!-- change order items details -->
          <!-- items -->
          <div class="border-t border-gray-300 pt-10 mt-10">
            <div>
              <h3 class="text-xl font-medium leading-10 tracking-tight">Details</h3>
            </div>

            <!-- change order items -->
            <div class="mt-3">
              <div
                v-for="(lineItems, category, index) in getChangeOrderItems"
                :key="category"
                class="mb-4"
              >
                <span class="font-semibold text-xl mb-3">{{ category }}</span>

                <div
                  v-for="(lineItem, idx) in lineItems"
                  :key="lineItem.id"
                  :class="[
                    idx == lineItems.length - 1 ? 'py-2' : 'border-b border-gray-200 py-2',
                    !lineItem.selected ? 'opacity-50' : ''
                  ]"
                >
                  <div>
                    <div class="grid grid-cols-5 gap-4">
                      <div class="col-span-4 flex items-start">
                        <div
                          v-if="lineItem.optional && changeOrder.status === PROPOSAL_STATUS.AWAITING_ACCEPTANCE"
                          class="pt-2 pr-1"
                        >
                          <!-- optional checkbox -->
                          <BaseCheckbox
                            v-model="lineItem.selected"
                            :disabled="preview"
                          />
                        </div>

                        <BaseCollapse
                          :bg-color="'bg-withe'"
                          :class="clsx(
                            'pt-0',
                            (lineItem.optional && changeOrder.status === PROPOSAL_STATUS.AWAITING_ACCEPTANCE) && 'pl-0',
                            (!lineItem.optional && changeOrder.status === PROPOSAL_STATUS.AWAITING_ACCEPTANCE) && 'pl-3',
                          )"
                          :default-open="idx === 0 && index === 0"
                        >
                          <template #header>
                            <div class="flex items-center">
                              <span class="font-bold">{{ lineItem.title }}</span>
                            </div>
                          </template>

                          <div class="text-justify" v-html="lineItem.scopeOfWork" />

                          <!-- attachments -->
                          <Lightbox :attachments="lineItem.attachments" />
                        </BaseCollapse>
                      </div>

                      <!-- totals -->
                      <div class="text-sm pt-2 font-semibold text-right">
                        <span>{{ currency(lineItem.totalPrice) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- change order total price -->
            <div class="mt-6">
              <div class="grid grid-cols-5 gap-4 border-t-2 border-gray-200 py-4">
                <div class="col-span-5 flex text-right justify-end">
                  <div class="mr-3">
                    <div>
                      <b>Total:</b>
                    </div>
                    <div>
                      <b>Previous Contract Amount:</b>
                    </div>
                    <div>
                      <b>Revised Contract Total:</b>
                    </div>
                  </div>
                  <div>
                    <div>
                      {{ currency(changeOrderTotalPrice) }}
                    </div>
                    <div>
                      {{ currency(previousContractAmount) }}
                    </div>
                    <div>
                      {{ currency(changeOrderTotalPrice + previousContractAmount) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>


          <!-- legal terms -->
          <div class="mt-6 text-justify" v-html="changeOrder.termsConditions" />
        </div>


        <!-- messages -->
        <div
          ref="revision-comment"
          class="mt-10 bg-white p-4 pb-10 shadow-md rounded-lg"
        >
          <h4 class="mb-4">Comments</h4>

          <!-- message thread -->
          <MessageThread
            v-if="messages"
            :key="`comments-${project.id}`"
            ref="comments"
            style="max-height: 360px; overflow-y: scroll;"
            :messages="messages"
            :message-type="MESSAGE_TYPES.COMMENT"
          />
          <MessageForm
            :project="project"
            :message-type="'comment'"
            :preview="preview"
            :save-funtion="addMessage"
          />
        </div>

      </div>
    </div>

    <Modal
      v-model="openSignatureModal"
      width="w-full md:max-w-md"
      :no-escape="true"
      :no-backdrop="true"
      :no-close-button="true"
    >
      <template #body>
        <div>
          <form @submit.stop.prevent="form.handleSubmit()">
            <h3 class="text-lg font-semibold">
              In agreement to the change order, I hereby submit my e-signature:
            </h3>

            <div class="mt-4 grid grid-cols-2 gap-4">
              <form.Field
                name="name"
                :validators="{
                  onChange: yup.string().required('Name is required')
                }"
              >
                <template #default="{ field }">
                  <BaseInput
                    :model-value="(field.state.value as string)"
                    :name="field.name"
                    label="Name"
                    :error="field.state.meta.errors && field.state.meta.errors[0]"
                    @update:model-value="(value) => field.handleChange(String(value))"
                  />
                </template>
              </form.Field>

              <BaseInputDate
                v-model="changeOrder.signature.timestamp"
                label="Date"
                :disabled="true"
              />
            </div>

            <p class="mt-4 text-xs text-gray-500">
              By clicking "Submit E-Signature" button you agree to electrocally sign the document presented.
            </p>

            <form.Subscribe>
              <template #default="{ canSubmit, isSubmitting }">
                <div class="flex justify-end gap-4 -mb-4 mt-6">
                  <BaseButton
                    variant="secondary"
                    type="button"
                    text="Cancel"
                    :disabled="isSubmitting"
                    :class="{ 'cursor-not-allowed opacity-50': isSubmitting }"
                    @click="closeSignatureModal"
                  />

                  <BaseButton
                    variant="primary"
                    type="submit"
                    text="Submit E-Signature"
                    :disabled="!canSubmit || isSubmitting"
                    :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting }"
                  >
                    <span
                      v-if="isSubmitting"
                      class="i-heroicons-arrow-path animate-spin"
                    />
                  </BaseButton>
                </div>
              </template>
            </form.Subscribe>
          </form>
        </div>
      </template>
    </Modal>
  </div>

  <div
    v-if="!isLoading && changeOrder.id"
    id="printContent"
    class="hide-print-content"
  >
    <PrintChangeOrder
      :change-order="changeOrder"
      :change-orders="changeOrders"
      :project="project"
      :customer="customer"
      :budget="budget"
      :proposal="proposal"
    />
  </div>
  <div
    v-if="!isLoading && changeOrder.id"
    id="downloadChangeOrder"
    class="no-print"
  >
    <DownloadChangeOrder
      v-show="!pdfReady"
      :change-order="changeOrder"
      :change-orders="changeOrders"
      :project="project"
      :customer="customer"
      :budget="budget"
      :proposal="proposal"
    />
  </div>
</template>

<script lang='ts' setup>
import { Budget, BudgetItem } from '@/models/budget'
import { Customer } from '@/models/customer'
import { Project } from '@/models/project'
import { Proposal, PROPOSAL_STATUS } from '@/models/proposal'
import FirebaseService from '@/services/FirebaseService'
import { extend, filter, contains } from 'underscore'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseCollapse from '@/components/base/BaseCollapse.vue'
import MessageForm from '@/modules/Project/components/ProjectMessage.vue'
import MessageThread from '@/modules/Project/components/ProjectMessageThread.vue'
import PrintChangeOrder from '../components/PrintChangeOrder.vue'
import DownloadChangeOrder from '../components/DownloadChangeOrder.vue'
import { Message, MESSAGE_TYPES } from '@/models/message'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import html2pdf from 'html2pdf.js'
import logo from '@/assets/images/logo.png'
import { httpsCallable } from 'firebase/functions'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { useRoute } from 'vue-router'
import { computed, ref, useTemplateRef, watch } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { useHead } from '@unhead/vue'
import BaseDropdown from '@/components/base/BaseDropdown.vue'
import { currency, sortLineItemsByCategoryName, toNumber } from '@/utils'
import ProjectService from '@/services/ProjectService'
import ProposalStatusBadge from '../components/ProposalStatusBadge.vue'
import Modal from '@/components/app/AppModal.vue'
import BaseInputDate from '@/components/base/BaseInputDate.vue'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import clsx from 'clsx'
import Lightbox from '@/components/app/AppLightBox.vue'
import { useAuth } from '@/composables/useAuth'

useHead({ title: 'Change Order' })

const { isAuthenticated } = useAuth()
const { notify } = useNotification()
const $route = useRoute()
const changeOrderId = $route.params.id as string
const proposalId = $route.params.proposalId as string
const preview = $route.query.preview === 'true' || false

const project = ref(new Project())
const customer = ref(new Customer())
const budget = ref(new Budget())
const proposal = ref(new Proposal())
const changeOrder = ref({
  ...new Proposal(),
  signature: {
    name: '',
    timestamp: new Date()
  }
})
const changeOrders = ref([] as Proposal[])

const { data: fetchedData, isLoading, isError } = useQuery({
  queryKey: ['customer/changeOrders', changeOrderId],
  queryFn: async () => {
    const functions = await FirebaseService.functions()
    const fetchData = httpsCallable(functions, 'proposal-fetchChangeOrderV2')
    const response = await fetchData({ changeOrderId, proposalId })

    try {
      const data = response.data as any
      changeOrder.value = extend(changeOrder.value, JSON.parse(JSON.stringify(data.changeOrder))) as Proposal
      project.value = extend(project.value, JSON.parse(JSON.stringify(data.project))) as Project
      budget.value = extend(budget.value, JSON.parse(JSON.stringify(data.budget))) as Budget
      proposal.value = extend(proposal.value, JSON.parse(JSON.stringify(data.proposal))) as Proposal
      customer.value = extend(customer.value, JSON.parse(JSON.stringify(data.customer))) as Customer
      changeOrders.value = extend([], JSON.parse(JSON.stringify(data.changeOrders))) as Proposal[]

      return { project: project.value, budget: budget.value, proposal: proposal.value, customer: customer.value, changeOrder: changeOrder.value, changeOrders: changeOrders.value }
    } catch (err) {
      console.log(err)
      notify({
        type: 'error',
        title: 'Error',
        text: 'Error trying to fetch data'
      })
    }
  }
})

watch(isLoading, (loading) => {
  if (!loading) {
    project.value = JSON.parse(JSON.stringify(fetchedData.value.project))
    budget.value = JSON.parse(JSON.stringify(fetchedData.value.budget))
    proposal.value = JSON.parse(JSON.stringify(fetchedData.value.proposal))
    customer.value = JSON.parse(JSON.stringify(fetchedData.value.customer))
    changeOrder.value = JSON.parse(JSON.stringify(fetchedData.value.changeOrder))
    changeOrders.value = JSON.parse(JSON.stringify(fetchedData.value.changeOrders))
  }
}, { immediate: true })

const getChangeOrderItems = computed(() => {
  const items = filter(budget.value.lineItems, (item) => contains(changeOrder.value.lineItems, item.id))
  const sortedItems = sortLineItemsByCategoryName(items)

  return sortedItems
})

const changeOrderTotalPrice = computed(() => {
  return budget.value.lineItems
    .filter(item => changeOrder.value.lineItems.includes(item.id) && item.selected)
    .reduce((total, item) => total + toNumber(item.totalPrice), 0)
})

const getLocalDate = computed(() => (date: Date) => {
  return new Date(date).toLocaleDateString()
})

const previousContractAmount = computed(() => {
  let total = 0
  budget.value.lineItems.forEach((item: BudgetItem) => {
    if (proposal.value.lineItems.includes(item.id) && item.selected) {
      total += toNumber(item.totalPrice)
    }
  })

  // other accepted change orders
  changeOrders.value.forEach((changeOrder) => {
    if (changeOrder.status === PROPOSAL_STATUS.ACCEPTED) {
      budget.value.lineItems.forEach((item: BudgetItem) => {
        if (changeOrder.lineItems.includes(item.id) && item.selected) {
          total += toNumber(item.totalPrice)
        }
      })
    }
  })
  return total
})

const openSignatureModal = ref(false)

const form = useForm({
  defaultValues: changeOrder.value.signature,
  validatorAdapter: yupValidator(),
  onSubmit: async ({ value }) => {
    changeOrder.value.signature.name = value.name
    await acceptChangeOrder()
    openSignatureModal.value = false
  }
})

const closeSignatureModal = () => {
  form.reset()
  openSignatureModal.value = false
}

const isPending = computed(() => decliningChange.value || acceptingChange.value)

const { mutateAsync: declineChangeOrder, isPending: decliningChange } = useMutation({
  mutationFn: async () => {
    try {
      const functions = await FirebaseService.functions()
      const decline = httpsCallable(functions, 'proposal-declineChangeOrderV2')

      changeOrder.value.status = PROPOSAL_STATUS.DECLINED
      await decline({ proposalId: proposal.value.id , changeOrderId: changeOrder.value.id })
    } catch (_) {
      changeOrder.value.status = PROPOSAL_STATUS.AWAITING_ACCEPTANCE
    }
  },
  onSuccess: () => {
    notify({
      type: 'success',
      title: 'Change Order declined',
      text: 'Change Order declined successfully'
    })
  },
  onError: () => {
    notify({
      type: 'error',
      title: 'Error',
      text: 'Error while trying to decline change order'
    })
  }
})

const { mutateAsync: acceptChangeOrder, isPending: acceptingChange } = useMutation({
  mutationFn: async () => {
    try {
      const functions = await FirebaseService.functions()
      const accept = httpsCallable(functions, 'proposal-acceptChangeOrderV2')
      const updatedChangeOrder = extend({}, changeOrder.value) as Proposal

      updatedChangeOrder.status = PROPOSAL_STATUS.ACCEPTED
      await accept({ changeOrder: updatedChangeOrder, budget: budget.value })

      changeOrder.value = updatedChangeOrder
    } catch (_) {
      changeOrder.value.status = PROPOSAL_STATUS.AWAITING_ACCEPTANCE
    }
  },
  onSuccess: () => {
    notify({
      type: 'success',
      title: 'Change Order accepted',
      text: 'Change Order accepted successfully'
    })
  },
  onError: () => {
    notify({
      type: 'error',
      title: 'Error',
      text: 'Error while trying to accept Change Order'
    })
  }
})

const enable = computed(() => !!project.value.id)
const projectService = new ProjectService()
const queryClient = useQueryClient()

// get customer messages
const { data: messages } = useQuery({
  queryKey: ['messages', proposalId],
  queryFn: async () => {
    try {
      const functions = await FirebaseService.functions()
      const fetchMessages = httpsCallable(functions, 'project-getProjectMessagesV2')
      const response = await fetchMessages({ projectId: project.value.id, customerId: project.value.customerId }) as any

      return response.data.messages
    } catch (error) {
      console.log(error, 'error customer messages')
      notify({
        type: 'error',
        title: 'Error',
        text: 'Error trying to fetch messages'
      })
      return []
    }
  },
  enabled: enable
})

const messageThreadRef = useTemplateRef('comments')
const { mutateAsync: addMessage } = useMutation({
  mutationFn: async (message: Message) => {
    if (!message.from.id) {
      message.from.id = project.value.customerId
      message.from.email = customer.value.email
      message.from.name = customer.value.name
    }

    const newMessage = await projectService.addMessageToProject(project.value.id, project.value.customerId, message)
    return newMessage
  },
  onSuccess: (newMessage) => {
    queryClient.setQueryData(['messages', project.value.id], [...messages.value, newMessage])

    notify({
      type: 'success',
      title: 'Message sent',
      text: 'Message sent successfully'
    })

    setTimeout(() => {
      messageThreadRef.value.$el.scrollTo({
        top: messageThreadRef.value.$el.scrollHeight,
        behavior: 'smooth'
      })
    }, 100)
  },
  onError: (error) => {
    console.log(error)
    notify({
      type: 'error',
      title: 'Error',
      text: 'Error while trying to send message'
    })
  }
})

const revisionCommentRef = useTemplateRef('revision-comment')
const gotoComment = () => {
  const element = revisionCommentRef.value
  const top = element.offsetTop

  window.scrollTo({
    top,
    behavior: 'smooth'
  })
}

const printChangeOrder = () => {
  window.print()
}

const pdfReady = ref(true)
const download = async () => {
  pdfReady.value = false
  const element = document.getElementById('downloadChangeOrder')
  const options = {
    margin: 0.5,
    filename: `Change-order-${project.value.name}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
  }

  html2pdf().from(element).set(options).save().then(() => {
    pdfReady.value = true
  })
}
</script>

<style lang="scss" scoped>
.not-selected-item {
  opacity: 0.6;
}

@media print {
  .no-print {
    display: none;
  }
  #printContent {
    display: block;
  }
}

.hide-print-content {
  display: none;
}
</style>

